// Daily Aptitude Drill — 5 Qs, instant explanations, end-of-day recap.
// One drill per (student, EAT date), surfaced from:
//   - Inbox CTA (notification: aptitude_daily_drill)
//   - Dashboard "Today's Drill" card
//   - Direct /aptitude-drill/today URL

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  DrillAnswerResponse,
  DrillCompleteResponse,
  DrillQuestion,
  DrillTodayResponse,
  aptitudeDrillAPI,
} from '../services/aptitudeDrillAPI';
import { track } from '../services/api';
import AIHelpPanel from '../components/MicroLearning/AIHelpPanel';
import { displayTopic } from '../config/topicDisplayNames';
import QuizCanvas, { ACCENTS } from '../components/ExamRun/QuizCanvas';
import { useTheme } from '../contexts/ThemeContext';

// Theme-aware palette. Light values are byte-for-byte the originals; dark
// values are tinted/brightened equivalents that read on a dark page.
const makeColors = (dark: boolean) => ({
  primary: dark ? '#86c232' : '#4a7c10',
  primaryDark: dark ? '#6fa029' : '#3a6209',
  border: dark ? '#262c35' : '#e2e8f0',
  bg: dark ? '#0f1216' : '#f8fafc',
  textPrimary: dark ? '#e6e8eb' : '#0f172a',
  textMuted: dark ? '#aab1ba' : '#64748b',
  correct: dark ? '#86c232' : '#16a34a',
  correctBg: dark ? 'rgba(134,194,50,0.12)' : '#f0fdf4',
  wrong: dark ? '#ff6b6b' : '#dc2626',
  wrongBg: dark ? 'rgba(255,107,107,0.15)' : '#fef2f2',
});

const AptitudeDrill: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  const [searchParams] = useSearchParams();
  const entryPoint = searchParams.get('from') || 'direct';

  const [drill, setDrill] = useState<DrillTodayResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Per-question local state
  const [selected, setSelected] = useState<string | null>(null);
  const [lastAnswer, setLastAnswer] = useState<DrillAnswerResponse | null>(null);
  const [qStartedAt, setQStartedAt] = useState<number>(Date.now());

  // End state
  const [recap, setRecap] = useState<DrillCompleteResponse | null>(null);

  // Ask Lomi
  const [showAIHelp, setShowAIHelp] = useState(false);

  // Streak signal for sessionState (consecutive wrong, reset on correct)
  const [consecutiveWrong, setConsecutiveWrong] = useState(0);

  // -------------- load -----------------

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await aptitudeDrillAPI.today();
        if (cancelled) return;
        setDrill(data);
        setQStartedAt(Date.now());
        track('aptitude_drill.opened', {
          entry_point: entryPoint,
          path_day_index: data.path?.day_index,
          status: data.status,
          answered: data.progress.answered,
          total: data.total_questions,
        });
        if (data.status === 'completed') {
          // Re-render recap from server state
          try {
            const c = await aptitudeDrillAPI.complete();
            if (!cancelled) setRecap(c);
          } catch {
            /* non-critical — fall back to the in-progress view */
          }
        }
      } catch (e: any) {
        const status = e?.response?.status;
        if (status === 401) {
          navigate('/login');
          return;
        }
        setLoadError(
          e?.response?.data?.detail || "Couldn't load today's drill. Try again in a moment.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, entryPoint]);

  // -------------- current question --------------
  //
  // currentIdx advances only on explicit Next-click (handleNext). Don't
  // derive it from drill.progress.answered_ids — after the 5th answer the
  // server reports answered_ids=5, which would push currentIdx past the
  // end and hide the explanation+Finish panel for the question they just
  // answered. The transition to the recap is owned by Next-click.

  const [currentIdx, setCurrentIdx] = useState(0);
  const initialResumeApplied = useRef(false);

  // On first load of a drill, resume at the first un-answered Q (covers
  // mid-session refresh). Subsequent drill state updates (e.g. progress
  // bumps from /answer) must NOT move the pointer.
  useEffect(() => {
    if (!drill || initialResumeApplied.current) return;
    const answered = new Set(drill.progress.answered_ids);
    const idx = drill.questions.findIndex(q => !answered.has(q.question_id));
    setCurrentIdx(idx === -1 ? drill.questions.length : idx);
    initialResumeApplied.current = true;
  }, [drill]);

  const currentQ: DrillQuestion | null =
    drill && currentIdx < drill.questions.length ? drill.questions[currentIdx] : null;

  const allDone = !!drill && currentIdx >= drill.total_questions;

  // -------------- handlers --------------

  const handleSelect = useCallback(
    async (letter: string) => {
      if (!currentQ || submitting || lastAnswer) return;
      setSelected(letter);
      setSubmitting(true);
      try {
        const elapsed = Math.max(0, Math.round((Date.now() - qStartedAt) / 1000));
        const resp = await aptitudeDrillAPI.answer(currentQ.question_id, letter, elapsed);
        setLastAnswer(resp);
        setConsecutiveWrong(prev => (resp.is_correct ? 0 : prev + 1));
        track('aptitude_drill.answered', {
          path_day_index: drill?.path?.day_index,
          question_id: currentQ.question_id,
          topic: currentQ.topic,
          q_number: currentIdx + 1,
          total: drill?.total_questions ?? 5,
          is_correct: resp.is_correct,
          time_spent_seconds: elapsed,
          selected: letter,
        });
        // Reflect new progress in the drill state so currentIdx advances
        // once the student taps "Next"
        setDrill(prev =>
          prev
            ? {
                ...prev,
                progress: resp.progress,
              }
            : prev,
        );
      } catch (e: any) {
        const detail = e?.response?.data?.detail || 'Could not submit answer';
        // Allow re-try on transient failures
        setSelected(null);
        setLoadError(detail);
      } finally {
        setSubmitting(false);
      }
    },
    [currentQ, submitting, lastAnswer, qStartedAt],
  );

  const handleNext = useCallback(async () => {
    setSelected(null);
    setLastAnswer(null);
    setQStartedAt(Date.now());
    setLoadError(null);
    setCurrentIdx(idx => idx + 1);
    // Reset Ask Lomi visibility for the next question — the floating
    // button only re-appears once they've tried that one too.
    setShowAIHelp(false);
  }, []);

  const finishIfDone = useCallback(async () => {
    if (!drill || recap) return;
    const answered = drill.progress.answered + (lastAnswer ? 0 : 0); // already counted via resp.progress
    if (answered < drill.total_questions) return;
    try {
      const c = await aptitudeDrillAPI.complete();
      setRecap(c);
      track('aptitude_drill.completed', {
        path_day_index: drill.path?.day_index,
        score: c.score,
        total: c.total,
        weakest_topic: c.weakest_today?.topic,
      });
    } catch {
      /* swallow — student can retry by reloading */
    }
  }, [drill, lastAnswer, recap]);

  useEffect(() => {
    if (allDone && !recap) finishIfDone();
  }, [allDone, recap, finishIfDone]);

  // -------------- views --------------

  if (loadError && !drill) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <div style={cardStyle(dark)}>
          <div style={{ color: COLORS.wrong, fontWeight: 600 }}>Something went wrong</div>
          <div style={{ marginTop: 6, color: COLORS.textMuted }}>{loadError}</div>
        </div>
      </Shell>
    );
  }

  if (!drill) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <div style={{ ...cardStyle(dark), color: COLORS.textMuted }}>Loading today's drill…</div>
      </Shell>
    );
  }

  if (recap) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <Recap
          recap={recap}
          onContinue={() => navigate('/dashboard')}
          onPastDrills={() => navigate('/aptitude-drill/history')}
        />
      </Shell>
    );
  }

  if (!currentQ) {
    // We've answered everything but completion hasn't returned yet
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <div style={{ ...cardStyle(dark), color: COLORS.textMuted }}>Wrapping up your drill…</div>
      </Shell>
    );
  }

  const qNumber = currentIdx + 1;
  const total = drill.total_questions;
  const topicLabel = displayTopic(currentQ.topic || '') || 'Aptitude';
  const contextLabel = [topicLabel, currentQ.year_ec || ''].filter(Boolean).join(' · ');

  // Ask Lomi context — same shape as PastESSLCEQuiz so the AI prompt builder
  // sees a familiar payload. drillContext adds the path-day + strategy hint.
  const sessionState = {
    timeOnComponentSeconds: Math.max(0, Math.round((Date.now() - qStartedAt) / 1000)),
    lastAnswer: selected || undefined,
    lastAnswerCorrect: lastAnswer ? lastAnswer.is_correct : undefined,
    consecutiveWrong,
    questionsAttempted: drill.progress.answered,
    questionsCorrect: drill.progress.correct,
  };
  const drillContext = drill.path
    ? { day_index: drill.path.day_index, strategy_title: drill.path.strategy_title || '' }
    : undefined;

  return (
    <QuizCanvas
      accent={ACCENTS.aptitude}
      onBack={() => navigate('/dashboard')}
      answered={drill.progress.answered}
      qNumber={qNumber}
      total={total}
      contextLabel={contextLabel}
      questionText={currentQ.question_text}
      media={currentQ.media}
      options={currentQ.options}
      selected={selected}
      lastAnswer={lastAnswer}
      submitting={submitting}
      onSelect={handleSelect}
      onNext={handleNext}
      isLast={qNumber === total}
      questionId={currentQ.question_id}
      translateSurface="aptitude_drill_explanation"
      aiHelp={
        lastAnswer ? (
          <AIHelpPanel
            context="esslce"
            source="web_aptitude_drill"
            questionId={currentQ.question_id}
            questionText={currentQ.question_text}
            sessionState={sessionState}
            drillContext={drillContext}
            isOpen={showAIHelp}
            onToggle={() => setShowAIHelp(v => !v)}
          />
        ) : null
      }
    />
  );
};

// ----------- sub-components -----------

interface ShellProgress {
  /** Number of questions already answered. */
  current: number;
  total: number;
  /** Zero-based index of the question currently on screen. */
  currentIdx?: number;
}

const Shell: React.FC<{
  children: React.ReactNode;
  onBack: () => void;
  progress?: ShellProgress;
  meta?: React.ReactNode;
}> = ({ children, onBack, progress, meta }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  return (
  <div style={{ minHeight: '100vh', background: COLORS.bg }}>
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        background: dark ? '#171b21' : 'white',
        borderBottom: `1px solid ${COLORS.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <button
        onClick={onBack}
        aria-label="Back"
        style={{
          background: 'transparent',
          border: 'none',
          padding: '6px 6px',
          cursor: 'pointer',
          color: dark ? '#aab1ba' : '#475569',
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        ←
      </button>
      {progress ? (
        <DotStepper {...progress} />
      ) : (
        <h1 style={{ margin: 0, fontSize: '1rem', color: COLORS.textPrimary }}>
          Today's Aptitude
        </h1>
      )}
      <div style={{ flex: 1 }} />
      {meta}
    </header>
    <main style={{ maxWidth: 720, margin: '0 auto', padding: '12px 16px 80px' }}>{children}</main>
  </div>
  );
};

const DotStepper: React.FC<ShellProgress> = ({ current, total, currentIdx }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  return (
  <div
    role="progressbar"
    aria-valuenow={current}
    aria-valuemin={0}
    aria-valuemax={total}
    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
  >
    {Array.from({ length: total }).map((_, i) => {
      const isDone = i < current;
      const isCurrent = currentIdx !== undefined ? i === currentIdx : false;
      const size = isCurrent ? 10 : 8;
      return (
        <span
          key={i}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: isDone ? COLORS.primary : 'transparent',
            border: `1.5px solid ${
              isDone ? COLORS.primary : isCurrent ? COLORS.primary : dark ? '#2d333b' : '#cbd5e1'
            }`,
            transition: 'all 180ms ease',
          }}
        />
      );
    })}
  </div>
  );
};

const Recap: React.FC<{
  recap: DrillCompleteResponse;
  onContinue: () => void;
  onPastDrills: () => void;
}> = ({ recap, onContinue, onPastDrills }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  const pct = recap.total > 0 ? Math.round((recap.score / recap.total) * 100) : 0;
  return (
    <div style={cardStyle(dark)}>
      <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.textPrimary }}>
        Today's drill: {recap.score} / {recap.total}
      </div>
      <div style={{ marginTop: 4, color: COLORS.textMuted, fontSize: 14 }}>
        {pct}% — drill {recap.drill_date}
      </div>

      {recap.weakest_today && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: dark ? 'rgba(245,176,65,0.12)' : '#fef9c3',
            border: `1px solid ${dark ? 'rgba(245,176,65,0.35)' : '#fde047'}`,
            borderRadius: 10,
            color: dark ? '#f5c563' : '#713f12',
          }}
        >
          <strong>Today's weakest:</strong>{' '}
          {displayTopic(recap.weakest_today.topic) || recap.weakest_today.topic} —{' '}
          {recap.weakest_today.correct}/{recap.weakest_today.answered}
        </div>
      )}

      {Object.keys(recap.by_topic).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>
            By topic
          </div>
          {Object.entries(recap.by_topic).map(([topic, b]) => (
            <div
              key={topic}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: `1px solid ${COLORS.border}`,
                fontSize: 14,
              }}
            >
              <span>{displayTopic(topic) || topic}</span>
              <span style={{ color: COLORS.textMuted }}>
                {b.correct}/{b.answered}
              </span>
            </div>
          ))}
        </div>
      )}

      <button onClick={onContinue} style={primaryBtnStyle(dark)}>
        Back to Dashboard
      </button>
      <button
        onClick={onPastDrills}
        style={{
          marginTop: 10,
          width: '100%',
          padding: '10px 14px',
          background: 'transparent',
          color: COLORS.textPrimary,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 10,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 500,
        }}
      >
        📚 Past drills
      </button>
      <div style={{ marginTop: 12, color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>
        New 5 questions tomorrow morning.
      </div>
    </div>
  );
};

// ----------- inline styles -----------

const cardStyle = (dark: boolean): React.CSSProperties => ({
  background: dark ? '#171b21' : 'white',
  border: `1px solid ${makeColors(dark).border}`,
  borderRadius: 14,
  padding: 16,
  boxShadow: dark ? '0 1px 0 rgba(0,0,0,0.4)' : '0 1px 0 rgba(0,0,0,0.02)',
});

const chipStyle: React.CSSProperties = {
  padding: '3px 10px',
  background: '#f1f5f9',
  color: '#475569',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
};

const metaStyle = (dark: boolean): React.CSSProperties => ({
  fontSize: 13,
  fontWeight: 600,
  color: makeColors(dark).textMuted,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '50vw',
});

const primaryBtnStyle = (dark: boolean): React.CSSProperties => ({
  marginTop: 12,
  width: '100%',
  padding: '12px 16px',
  background: makeColors(dark).primary,
  color: dark ? '#0f1216' : 'white',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
});

export default AptitudeDrill;
